import { useMemo, useState } from "react";
import { T, fontDisplay, fontMono } from "../theme";
import { PRIORITIES, PRIORITY, priorityRank } from "../engine/priorities";
import { addDays, daysLate, expectedPaymentDate, fmtDay, fmtMoney, isoDate, parseDate, todayStr } from "../engine/dates";
import { savePaymentDecision, clearPaymentDecision, addInstalments, updateInstalment, deleteInstalment, deleteManualEntry, dismissDoc, markDocPaid, saveExpectedDate, undismissDoc, unmarkDocPaid, updateManualEntry } from "../lib/data";

/**
 * Everything owed to a supplier, in one place, built for scheduling.
 *
 * The week drawer could not do this job: it only reaches one week at a time,
 * and it could only move Scoro documents, so forty-five of forty-nine imported
 * bills had no checkbox at all. Deciding who gets paid when is a job done
 * across the whole list at once, sorted by what matters at the time — the
 * oldest, the largest, or one supplier's worth.
 */
/**
 * Every agreed payment plan, in one place.
 *
 * A plan is the strongest statement this tool holds about money going out —
 * somebody negotiated it — so it deserves to be readable and changeable
 * without hunting through a list of bills for the ones that happen to have
 * instalments.
 *
 * The check that earns its place here is whether a plan still adds up. Pay one
 * instalment early, agree a different figure, delete a line, and the plan
 * quietly stops matching the balance it was meant to clear — leaving money
 * that is owed but appears nowhere. That is flagged per plan rather than
 * silently corrected, because which of the two numbers is wrong is a question
 * only the person who agreed it can answer.
 */
function PaymentPlans({ items, busy, onChange }) {
  const [adding, setAdding] = useState({});

  if (!items.length) {
    return (
      <div style={{ background: T.panel, borderRadius: 10, padding: 20, fontSize: 13, color: T.muted }}>
        No payment plans yet. Select bills on the unpaid list and use{" "}
        <strong style={{ color: T.ink }}>split into</strong> to agree one.
      </div>
    );
  }

  const totalPlanned = items.reduce(
    (s, i) => s + i.instalments.filter((x) => !x.paid).reduce((t, x) => t + Number(x.amount || 0), 0),
    0
  );
  const cell = { fontFamily: fontMono, fontSize: 11, padding: "3px 5px", border: `1px solid ${T.border}`, borderRadius: 4 };

  return (
    <div>
      <div style={{ fontSize: 12, color: T.muted, marginBottom: 10 }}>
        {items.length} bill{items.length === 1 ? "" : "s"} on an agreed plan ·{" "}
        <strong style={{ color: T.ink }}>{fmtMoney(totalPlanned)}</strong> still to pay across them
      </div>

      {items.map((i) => {
        const planned = i.instalments.reduce((t, x) => t + Number(x.amount || 0), 0);
        const outstanding = i.amount;
        const gap = Math.round((outstanding - planned) * 100) / 100;
        const left = i.instalments.filter((x) => !x.paid);
        return (
          <div
            key={i.key}
            style={{
              background: T.panel,
              borderRadius: 10,
              padding: 14,
              marginBottom: 10,
              border: `1px solid ${Math.abs(gap) > 0.01 ? T.warn : T.borderSoft}`,
            }}
          >
            <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
              <strong style={{ fontSize: 14 }}>{i.supplier}</strong>
              {i.docNo && (
                <span style={{ fontFamily: fontMono, fontSize: 11, color: T.muted }}>{i.docNo}</span>
              )}
              <span style={{ fontFamily: fontMono, fontSize: 13, color: T.cost }}>
                {fmtMoney(-outstanding)}
              </span>
              <span style={{ fontSize: 11, color: T.muted }}>
                {left.length} of {i.instalments.length} still to pay
              </span>
            </div>

            {Math.abs(gap) > 0.01 && (
              <div style={{ fontSize: 11, color: T.warn, marginTop: 5 }}>
                The plan comes to {fmtMoney(planned)} against a balance of {fmtMoney(outstanding)} —{" "}
                {gap > 0
                  ? `${fmtMoney(gap)} of this bill is not scheduled anywhere.`
                  : `${fmtMoney(-gap)} more is scheduled than is owed.`}
              </div>
            )}

            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8 }}>
              <tbody>
                {i.instalments.map((x, n) => (
                  <tr key={x.id} style={{ borderTop: n ? `1px solid ${T.borderSoft}` : "none" }}>
                    <td style={{ padding: "5px 8px 5px 0", width: 26, fontSize: 11, color: T.muted }}>
                      {n + 1}
                    </td>
                    <td style={{ padding: "5px 8px 5px 0", width: 150 }}>
                      <input
                        type="date"
                        key={`${x.id}-${x.due_date}`}
                        defaultValue={x.due_date || ""}
                        disabled={busy || x.paid}
                        style={cell}
                        onBlur={(e) => {
                          const v = e.target.value;
                          if (!v || v === x.due_date) return;
                          onChange(() => updateInstalment(x.id, { due_date: v }));
                        }}
                      />
                    </td>
                    <td style={{ padding: "5px 8px 5px 0", width: 120 }}>
                      <input
                        key={`${x.id}-${x.amount}`}
                        defaultValue={x.amount}
                        disabled={busy || x.paid}
                        style={{ ...cell, width: 90, textAlign: "right" }}
                        onBlur={(e) => {
                          const v = Number(e.target.value);
                          if (!v || v === Number(x.amount)) return;
                          onChange(() => updateInstalment(x.id, { amount: v }));
                        }}
                      />
                    </td>
                    <td style={{ padding: "5px 8px", fontSize: 11, color: T.muted }}>{x.note}</td>
                    <td style={{ padding: "5px 8px", textAlign: "right", whiteSpace: "nowrap" }}>
                      <label style={{ fontSize: 11, color: x.paid ? T.settled : T.muted, marginRight: 10 }}>
                        <input
                          type="checkbox"
                          checked={!!x.paid}
                          disabled={busy}
                          onChange={(e) => onChange(() => updateInstalment(x.id, { paid: e.target.checked }))}
                          style={{ marginRight: 4 }}
                        />
                        paid
                      </label>
                      <button
                        disabled={busy}
                        title="Remove this payment from the plan"
                        onClick={() => onChange(() => deleteInstalment(x.id))}
                        style={{ border: "none", background: "none", color: T.muted, cursor: "pointer", fontSize: 13 }}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Adding one by hand covers what a even split cannot: a supplier
                who wants more up front, or a final payment that clears a gap. */}
            <div style={{ marginTop: 8, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              <input
                type="date"
                value={adding[i.key]?.date || ""}
                onChange={(e) => setAdding({ ...adding, [i.key]: { ...adding[i.key], date: e.target.value } })}
                style={cell}
              />
              <input
                placeholder={gap > 0.01 ? String(gap) : "amount"}
                value={adding[i.key]?.amount || ""}
                onChange={(e) => setAdding({ ...adding, [i.key]: { ...adding[i.key], amount: e.target.value } })}
                style={{ ...cell, width: 90, textAlign: "right" }}
              />
              <button
                disabled={busy || !adding[i.key]?.date || !Number(adding[i.key]?.amount)}
                onClick={() =>
                  onChange(async () => {
                    await addInstalments([
                      {
                        doc_source: docSource(i),
                        doc_id: i.id,
                        due_date: adding[i.key].date,
                        amount: Number(adding[i.key].amount),
                        paid: false,
                        note: "added to the plan",
                      },
                    ]);
                    setAdding({ ...adding, [i.key]: null });
                  })
                }
                style={{
                  padding: "4px 11px",
                  borderRadius: 6,
                  border: "none",
                  background: adding[i.key]?.date && Number(adding[i.key]?.amount) ? T.accent : T.border,
                  color: adding[i.key]?.date && Number(adding[i.key]?.amount) ? "#fff" : T.muted,
                  fontSize: 11,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Add a payment
              </button>
              {gap > 0.01 && (
                <span style={{ fontSize: 11, color: T.muted }}>
                  {fmtMoney(gap)} still unscheduled
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * One bill's decision: how it is being treated, and why.
 *
 * The reason is not optional and the control says so — an empty box with a
 * chosen priority saves nothing. A label anybody can click is a label nobody
 * has thought about, and the whole point of this is to be able to answer the
 * question afterwards.
 */
function Decision({ item, busy, onSave, onClear }) {
  const [open, setOpen] = useState(false);
  const [priority, setPriority] = useState(item.decision?.priority || "");
  const [reason, setReason] = useState(item.decision?.reason || "");
  const current = item.decision ? PRIORITY[item.decision.priority] : null;

  if (!open) {
    return (
      <button
        onClick={() => {
          setPriority(item.decision?.priority || "");
          setReason(item.decision?.reason || "");
          setOpen(true);
        }}
        title={item.decision ? item.decision.reason : "Say how this is being treated, and why"}
        style={{
          border: "none",
          background: "none",
          padding: 0,
          textAlign: "left",
          cursor: "pointer",
          fontFamily: "inherit",
          maxWidth: 220,
        }}
      >
        {current ? (
          <>
            <span style={{ fontSize: 11, fontWeight: 700, color: current.tone }}>{current.label}</span>
            <div
              style={{
                fontSize: 10,
                color: T.muted,
                lineHeight: 1.45,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {item.decision.reason}
            </div>
          </>
        ) : (
          <span style={{ fontSize: 11, color: T.muted, textDecoration: "underline" }}>
            give a reason
          </span>
        )}
      </button>
    );
  }

  const ready = priority && reason.trim().length >= 3;
  return (
    <div style={{ minWidth: 220 }}>
      <select
        value={priority}
        onChange={(e) => setPriority(e.target.value)}
        style={{ fontFamily: "inherit", fontSize: 11, padding: "3px 5px", width: "100%" }}
      >
        <option value="">How is this being treated?</option>
        {PRIORITIES.map((p) => (
          <option key={p.key} value={p.key}>
            {p.label} — {p.blurb}
          </option>
        ))}
      </select>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Why. This is what gets handed to the FD."
        rows={2}
        style={{
          fontFamily: "inherit",
          fontSize: 11,
          padding: "4px 6px",
          width: "100%",
          boxSizing: "border-box",
          marginTop: 4,
          borderRadius: 4,
          border: `1px solid ${T.border}`,
          resize: "vertical",
        }}
      />
      <div style={{ display: "flex", gap: 6, marginTop: 4, alignItems: "center" }}>
        <button
          disabled={busy || !ready}
          onClick={async () => {
            await onSave(item, priority, reason);
            setOpen(false);
          }}
          style={{
            padding: "3px 10px",
            borderRadius: 5,
            border: "none",
            background: ready ? T.accent : T.border,
            color: ready ? "#fff" : T.muted,
            fontSize: 11,
            cursor: ready ? "pointer" : "default",
            fontFamily: "inherit",
          }}
        >
          Save
        </button>
        <button
          onClick={() => setOpen(false)}
          style={{ border: "none", background: "none", color: T.muted, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}
        >
          cancel
        </button>
        {item.decision && (
          <button
            onClick={async () => {
              await onClear(item);
              setOpen(false);
            }}
            style={{ border: "none", background: "none", color: T.cost, fontSize: 11, cursor: "pointer", fontFamily: "inherit", marginLeft: "auto" }}
          >
            clear
          </button>
        )}
      </div>
    </div>
  );
}

/** Which table a bill's marks, dates and plans are filed against. */
const docSource = (item) => (item.kind === "xero" ? "xero_bill" : "scoro_bill");

export default function BillsView({ scoroBills, xeroBills = [], manualEntries, expectedDates, paidMarks, dismissals = {}, instalments = [], decisions = {}, session, onChanged }) {
  const [sel, setSel] = useState({});
  const [sort, setSort] = useState("date");
  const [filter, setFilter] = useState("open");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [bulkDate, setBulkDate] = useState("");
  const [query, setQuery] = useState("");
  const [plan, setPlan] = useState({ count: 3, every: "month", from: "", first: "" });
  const [importing, setImporting] = useState(null);

  // Payment plans, by document. A bill being paid in agreed instalments is
  // scheduled just as firmly as one with a single date on it — more so, since
  // somebody had to agree it — so it belongs on the planned side of the split.
  const planByDoc = useMemo(() => {
    const by = {};
    for (const it of instalments) {
      (by[`${it.doc_source}:${it.doc_id}`] = by[`${it.doc_source}:${it.doc_id}`] || []).push(it);
    }
    for (const k of Object.keys(by)) by[k].sort((a, b) => String(a.due_date).localeCompare(String(b.due_date)));
    return by;
  }, [instalments]);
  const [confirming, setConfirming] = useState(null);
  const today = todayStr();

  // One shape for both sources, so the list does not care where a bill came
  // from — only what is owed, to whom, and when it is going out.
  const items = useMemo(() => {
    const out = [];

    // Xero owns what goes out. Scoro's bills stand down as soon as Xero has
    // any, in the same one-way handover the forecast makes, so the two lists
    // can never disagree about what is owed.
    for (const b of xeroBills) {
      const mark = paidMarks?.[`xero_bill:${b.id}`];
      const planned = expectedDates?.[`xero_bill:${b.id}`];
      const amount = Number(b.amount_due) || 0;
      if (!amount) continue;
      out.push({
        key: `xero:${b.id}`,
        kind: "xero",
        id: String(b.id),
        supplier: b.contact_name || "—",
        docNo: b.number || b.reference,
        description: b.source_note || b.reference,
        amount,
        dueDate: b.due_date,
        daysLate: daysLate(b.due_date, today),
        expected: mark ? mark.paid_date : expectedPaymentDate(b.due_date, planned, today),
        overridden: !!planned,
        instalments: planByDoc[`xero_bill:${b.id}`] || [],
        decision: decisions[`xero_bill:${b.id}`] || null,
        // The distinction this page exists for: a date somebody chose, or a
        // date the forecast had to invent because nobody has decided yet.
        planned: !!planned || !!mark || !!planByDoc[`xero_bill:${b.id}`]?.length,
        paid: !!mark,
        dismissed: !!dismissals?.[`xero_bill:${b.id}`],
      });
    }

    for (const b of xeroBills.length ? [] : scoroBills) {
      if ((b.currency || "GBP") !== "GBP") continue;
      const mark = paidMarks?.[`scoro_bill:${b.id}`];
      const dismissed = !!dismissals?.[`scoro_bill:${b.id}`];
      out.push({
        key: `scoro:${b.id}`,
        kind: "scoro",
        id: String(b.id),
        supplier: b.company_name || "—",
        docNo: b.no,
        description: b.description,
        amount: Number(b.outstanding) || 0,
        dueDate: b.deadline,
        daysLate: daysLate(b.deadline, today),
        expected: mark
          ? mark.paid_date
          : expectedPaymentDate(b.deadline, expectedDates?.[`bill:${b.id}`], today),
        overridden: !!expectedDates?.[`bill:${b.id}`],
        planned: !!expectedDates?.[`bill:${b.id}`] || !!mark,
        paid: !!mark,
        dismissed,
      });
    }
    for (const m of manualEntries) {
      if (m.direction !== "out") continue;
      if (m.merged_into) continue; // the real bill from Xero carries it now
      if (m.label === "HMRC" || m.own_row) continue; // these have their own lines
      out.push({
        key: `manual:${m.id}`,
        kind: "manual",
        id: m.id,
        supplier: m.label,
        docNo: (m.note || "").split(" · ")[0],
        description: m.note,
        amount: Number(m.amount) || 0,
        dueDate: null,
        daysLate: /(\d+) days overdue/.exec(m.note || "")?.[1] ? Number(/(\d+) days overdue/.exec(m.note)[1]) : null,
        expected: m.entry_date,
        overridden: false,
        // A hand-entered bill was given its date by whoever typed it, so it is
        // planned by construction — that is the only reason it has a date.
        planned: true,
        paid: !!m.received,
        dismissed: false,
      });
    }
    return out;
  }, [scoroBills, xeroBills, manualEntries, expectedDates, paidMarks, dismissals, planByDoc, decisions, today]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    // A bill gets looked for by whatever the person has in front of them: the
    // supplier, the number on the invoice, a word from the description, or the
    // amount they can see on a statement.
    const matches = (i) =>
      !q ||
      [i.supplier, i.docNo, i.description, Math.round(i.amount)]
        .filter((v) => v != null && v !== "")
        .some((v) => String(v).toLowerCase().includes(q));

    // Searching looks everywhere. Typing a supplier's name and being told
    // there are no bills, because the one you wanted was paid last week and
    // the filter still said unpaid, is worse than useless.
    const passes = (i) => {
      if (filter === "open") return !i.paid && !i.dismissed;
      if (filter === "paid") return i.paid;
      // The bills nobody has decided about: still owed, and sitting on
      // whatever date the forecast had to assume for them.
      if (filter === "unplanned") return !i.paid && !i.dismissed && !i.planned;
      if (filter === "plans") return !!i.instalments?.length;
      // The list to work through before the next conversation about cash.
      // A bill on an agreed plan is not on it: the plan is the decision, and a
      // supplier agreed to it, which is a stronger answer than a label.
      if (filter === "undecided")
        return !i.paid && !i.dismissed && !i.decision && !i.instalments?.length;
      return true;
    };
    let list = items.filter((i) => matches(i) && (q ? true : passes(i)));
    list = [...list].sort((a, b) => {
      if (sort === "amount") return b.amount - a.amount;
      if (sort === "supplier") return a.supplier.localeCompare(b.supplier) || b.amount - a.amount;
      if (sort === "priority")
        return priorityRank(a.decision?.priority) - priorityRank(b.decision?.priority) || b.amount - a.amount;
      if (sort === "late") return (b.daysLate || 0) - (a.daysLate || 0);
      return String(a.expected || "").localeCompare(String(b.expected || "")) || b.amount - a.amount;
    });
    return list;
  }, [items, sort, filter, query]);

  /**
   * Put the selected bills on a payment plan.
   *
   * Each bill is split across the instalments, with the remainder on the last
   * one so the plan adds up to the penny rather than leaving a few pence
   * unaccounted for. Every bill gets its own schedule from the same start
   * date — the alternative, one shared pot paid down across suppliers, is not
   * a thing any supplier would agree to.
   *
   * A first payment can be named, because that is how these are actually
   * agreed: something up front to show good faith, then the balance. Give it
   * an amount and two instalments and you get "£3,500 this week, the rest next
   * week"; leave it blank and the bill divides evenly, which is the other
   * thing suppliers agree to and nothing in between.
   *
   * A bill that already has a plan is skipped rather than doubled. Changing an
   * existing plan is a per-bill decision and belongs in its drawer, where the
   * instalments can be seen.
   */
  const schedule = async () => {
    const count = Math.max(1, Math.min(36, Number(plan.count) || 1));
    const start = plan.from || todayStr();
    const targets = chosen.filter((i) => i.kind !== "manual" && !i.instalments?.length);
    if (!targets.length) {
      setError(
        chosen.some((i) => i.instalments?.length)
          ? "Those already have a plan — open one to change it."
          : "Payment plans apply to bills, not hand-entered lines."
      );
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const rows = [];
      const firstAsked = Math.round((Number(plan.first) || 0) * 100);
      for (const item of targets) {
        const pennies = Math.round(item.amount * 100);
        // A named first payment larger than the bill is just paying the bill.
        const upFront = Math.min(firstAsked, pennies);
        const rest = pennies - upFront;
        const laterCount = upFront ? count - 1 : count;
        const each = laterCount > 0 ? Math.floor(rest / laterCount) : 0;

        for (let n = 0; n < count; n++) {
          let amount;
          if (upFront && n === 0) amount = upFront / 100;
          else {
            const idx = upFront ? n - 1 : n;
            const last = idx === laterCount - 1;
            amount = (last ? rest - each * (laterCount - 1) : each) / 100;
          }
          if (!amount) continue;
          const when = new Date(`${start}T00:00:00Z`);
          if (plan.every === "week") when.setUTCDate(when.getUTCDate() + n * 7);
          else if (plan.every === "fortnight") when.setUTCDate(when.getUTCDate() + n * 14);
          else when.setUTCMonth(when.getUTCMonth() + n);
          rows.push({
            doc_source: docSource(item),
            doc_id: item.id,
            due_date: isoDate(when),
            amount,
            paid: false,
            note:
              upFront && n === 0
                ? `agreed plan · first payment of ${count}`
                : `agreed plan · ${n + 1} of ${count}`,
          });
        }
      }
      await addInstalments(rows);
      setSel({});
      setPlan({ ...plan, first: "" });
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const saveDecision = async (item, priority, reason) => {
    setBusy(true);
    setError(null);
    try {
      await savePaymentDecision(docSource(item), item.id, priority, reason, session?.user?.email);
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const dropDecision = async (item) => {
    setBusy(true);
    try {
      await clearPaymentDecision(docSource(item), item.id);
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const chosen = shown.filter((i) => sel[i.key]);
  const chosenTotal = chosen.reduce((s, i) => s + i.amount, 0);
  const openTotal = items.filter((i) => !i.paid).reduce((s, i) => s + i.amount, 0);
  const overdue = items.filter((i) => !i.paid && (i.daysLate || 0) > 0);

  const setDate = async (item, date) => {
    if (item.kind !== "manual") await saveExpectedDate(docSource(item), item.id, date);
    else await updateManualEntry(item.id, { entry_date: date });
  };

  const applyDate = async (date) => {
    if (!chosen.length || !date) return;
    setBusy(true);
    setError(null);
    try {
      for (const it of chosen) await setDate(it, date);
      setSel({});
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  // Relative moves shift each bill from its own date, so a spread stays a spread
  // rather than collapsing onto one day and inventing a spike.
  const pushBy = async (days) => {
    if (!chosen.length) return;
    setBusy(true);
    setError(null);
    try {
      for (const it of chosen) {
        const base = parseDate(it.expected) || parseDate(today);
        await setDate(it, isoDate(addDays(base, days)));
      }
      setSel({});
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const togglePaid = async (item, paid) => {
    setBusy(true);
    try {
      if (item.kind === "scoro") {
        if (paid) await markDocPaid(docSource(item), item.id, todayStr());
        else await unmarkDocPaid(docSource(item), item.id);
      } else {
        await updateManualEntry(item.id, { received: paid });
      }
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const markChosenPaid = async () => {
    setBusy(true);
    try {
      for (const it of chosen) await togglePaidRaw(it);
      setSel({});
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };
  const togglePaidRaw = async (it) => {
    if (it.kind !== "manual") await markDocPaid(docSource(it), it.id, todayStr());
    else await updateManualEntry(it.id, { received: true });
  };

  /**
   * Removing a bill means two different things.
   *
   * An imported one is ours, so it is deleted outright. A Scoro one is a mirror
   * the sync rewrites hourly — deleting it here would see it return within the
   * hour and look like the delete had failed — so it is recorded as dismissed
   * and skipped, and stays in Scoro where it can actually be corrected.
   */
  const removeOne = async (item) => {
    setBusy(true);
    setError(null);
    try {
      if (item.kind === "manual") await deleteManualEntry(item.id);
      else await dismissDoc(docSource(item), item.id, "dismissed from the bills list");
      setConfirming(null);
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const removeChosen = async () => {
    setBusy(true);
    setError(null);
    try {
      for (const it of chosen) {
        if (it.kind === "manual") await deleteManualEntry(it.id);
        else await dismissDoc(docSource(it), it.id, "dismissed from the bills list");
      }
      setSel({});
      setConfirming(null);
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const btn = {
    padding: "5px 10px",
    fontSize: 11,
    borderRadius: 6,
    border: `1px solid ${T.border}`,
    background: "transparent",
    cursor: "pointer",
    fontFamily: "inherit",
  };
  const th = { padding: "8px 10px", textAlign: "left", fontSize: 11, color: T.muted, fontWeight: 500 };

  return (
    <div>
      <h2 style={{ fontFamily: fontDisplay, fontSize: 22, marginBottom: 4 }}>Supplier bills</h2>
      <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, maxWidth: 680 }}>
        Everything owed, from Scoro and from imports together. Change a date inline, or select any
        number and move them at once. Deciding who gets paid when is done across the whole list,
        not one week at a time. Removing an imported bill deletes it; removing a Scoro one keeps it
        out of the forecast but leaves it in Scoro, since the sync would bring a deletion straight
        back.
      </p>

      <div style={{ display: "flex", gap: 12, margin: "14px 0", flexWrap: "wrap" }}>
        <div style={{ background: T.panel, borderRadius: 10, padding: "12px 16px" }}>
          <div style={{ fontSize: 10, textTransform: "uppercase", color: T.muted, letterSpacing: 0.8 }}>
            Still to pay
          </div>
          <div style={{ fontFamily: fontDisplay, fontSize: 24 }}>{fmtMoney(openTotal)}</div>
          <div style={{ fontSize: 11, color: T.muted }}>
            {items.filter((i) => !i.paid).length} bills
          </div>
        </div>
        <div style={{ background: T.panel, borderRadius: 10, padding: "12px 16px" }}>
          <div style={{ fontSize: 10, textTransform: "uppercase", color: T.muted, letterSpacing: 0.8 }}>
            Overdue
          </div>
          <div style={{ fontFamily: fontDisplay, fontSize: 24, color: T.overdue }}>
            {fmtMoney(overdue.reduce((s, i) => s + i.amount, 0))}
          </div>
          <div style={{ fontSize: 11, color: T.muted }}>
            {overdue.length} bills · oldest {Math.max(0, ...overdue.map((i) => i.daysLate || 0))} days
          </div>
        </div>
      </div>

      <div data-scroll-x style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Supplier, bill number, description, amount…"
          style={{
            fontFamily: "inherit",
            fontSize: 12,
            padding: "7px 10px",
            width: 300,
            borderRadius: 6,
            border: `1px solid ${T.border}`,
          }}
        />
        <button
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              const { exportDecisions } = await import("../lib/exportExcel");
              await exportDecisions({
                items: shown.filter((i) => !i.paid && !i.dismissed),
                priorities: PRIORITIES,
                asAt: fmtDay(todayStr()),
                preparedBy: session?.user?.email,
              });
            } catch (e) {
              setError(e.message || String(e));
            } finally {
              setBusy(false);
            }
          }}
          style={{ ...btn, marginLeft: "auto" }}
          title="A workbook arguing the case for each bill — for a finance director or an investor"
        >
          Export the case
        </button>
        <label style={{ ...btn, cursor: "pointer" }} title="Read decisions back in from a workbook that has been worked on elsewhere">
          Import
          <input
            type="file"
            accept=".xlsx"
            style={{ display: "none" }}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (!file) return;
              setBusy(true);
              setError(null);
              try {
                const [{ readDecisions, matchDecisions }] = await Promise.all([
                  import("../lib/importDecisions"),
                ]);
                const { rows, problems } = await readDecisions(file);
                const { matched, unmatched } = matchDecisions(rows, items);
                setImporting({ matched, unmatched, problems });
              } catch (err) {
                setError(err.message || String(err));
              } finally {
                setBusy(false);
              }
            }}
          />
        </label>
        {query && (
          <>
            <button
              onClick={() => setQuery("")}
              style={{ border: "none", background: "none", color: T.muted, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}
            >
              clear
            </button>
            <span style={{ fontSize: 11, color: T.muted }}>
              {shown.length} of {items.length} · paid and unpaid
            </span>
          </>
        )}
      </div>

      <div data-scroll-x style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
        <span style={{ fontSize: 11, color: T.muted }}>Sort</span>
        {[
          ["date", "payment date"],
          ["late", "most overdue"],
          ["amount", "largest"],
          ["supplier", "supplier"],
          ["priority", "how it is treated"],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setSort(k)}
            style={{ ...btn, background: sort === k ? T.accentSoft : "transparent", borderColor: sort === k ? T.accent : T.border }}
          >
            {l}
          </button>
        ))}
        <span style={{ fontSize: 11, color: T.muted, marginLeft: 10 }}>Show</span>
        {[
          ["open", "unpaid"],
          ["unplanned", "no date planned"],
          ["plans", "payment plans"],
          ["undecided", "no reason given"],
          ["paid", "paid"],
          ["all", "all"],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            style={{ ...btn, background: filter === k ? T.accentSoft : "transparent", borderColor: filter === k ? T.accent : T.border }}
          >
            {l}
          </button>
        ))}
      </div>

      {chosen.length > 0 && (
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 5,
            background: T.panelSoft,
            border: `1px solid ${T.accent}`,
            borderRadius: 8,
            padding: 10,
            marginBottom: 10,
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <strong style={{ fontSize: 12 }}>{chosen.length} selected</strong>
          <span style={{ fontFamily: fontMono, fontSize: 12, color: T.cost }}>
            {fmtMoney(-chosenTotal)}
          </span>
          <span style={{ fontSize: 11, color: T.muted, marginLeft: 6 }}>Push out</span>
          <button style={btn} disabled={busy} onClick={() => pushBy(7)}>+1 week</button>
          <button style={btn} disabled={busy} onClick={() => pushBy(14)}>+2 weeks</button>
          <button style={btn} disabled={busy} onClick={() => pushBy(28)}>+4 weeks</button>
          <span style={{ fontSize: 11, color: T.muted }}>or all on</span>
          <input
            type="date"
            value={bulkDate}
            onChange={(e) => setBulkDate(e.target.value)}
            style={{ fontFamily: fontMono, fontSize: 11, padding: "3px 5px" }}
          />
          <button
            style={{ ...btn, background: bulkDate ? T.accent : "transparent", color: bulkDate ? "#fff" : T.muted, border: "none" }}
            disabled={busy || !bulkDate}
            onClick={() => applyDate(bulkDate)}
          >
            Move {chosen.length}
          </button>
          <span style={{ fontSize: 11, color: T.muted, marginLeft: 6 }}>or split into</span>
          <input
            type="number"
            min={1}
            max={36}
            value={plan.count}
            onChange={(e) => setPlan({ ...plan, count: e.target.value })}
            style={{ fontFamily: fontMono, fontSize: 11, padding: "3px 5px", width: 46 }}
          />
          <select
            value={plan.every}
            onChange={(e) => setPlan({ ...plan, every: e.target.value })}
            style={{ fontFamily: "inherit", fontSize: 11, padding: "3px 5px" }}
          >
            <option value="month">monthly</option>
            <option value="fortnight">fortnightly</option>
            <option value="week">weekly</option>
          </select>
          <span style={{ fontSize: 11, color: T.muted }}>first</span>
          <input
            value={plan.first}
            onChange={(e) => setPlan({ ...plan, first: e.target.value })}
            placeholder="even"
            title="Pay this much up front, then the balance across the rest. Leave blank to divide evenly."
            style={{ fontFamily: fontMono, fontSize: 11, padding: "3px 5px", width: 62, textAlign: "right" }}
          />
          <span style={{ fontSize: 11, color: T.muted }}>from</span>
          <input
            type="date"
            value={plan.from}
            onChange={(e) => setPlan({ ...plan, from: e.target.value })}
            style={{ fontFamily: fontMono, fontSize: 11, padding: "3px 5px" }}
          />
          <button
            style={{ ...btn, background: plan.from ? T.accent : "transparent", color: plan.from ? "#fff" : T.muted, border: plan.from ? "none" : `1px solid ${T.border}` }}
            disabled={busy || !plan.from}
            onClick={schedule}
          >
            {busy ? "Scheduling…" : "Schedule"}
          </button>
          <button style={{ ...btn, marginLeft: "auto" }} disabled={busy} onClick={markChosenPaid}>
            Mark {chosen.length} paid
          </button>
          <button
            style={{ ...btn, borderColor: T.cost, color: T.cost }}
            disabled={busy}
            onClick={() => (confirming === "bulk" ? removeChosen() : setConfirming("bulk"))}
          >
            {confirming === "bulk" ? `Really remove ${chosen.length}?` : `Remove ${chosen.length}`}
          </button>
          <button style={btn} onClick={() => { setSel({}); setConfirming(null); }}>clear</button>
        </div>
      )}

      {error && <div style={{ color: T.cost, fontSize: 12, marginBottom: 8 }}>{error}</div>}

      {/* Nothing is written until it has been looked at. An import that applies
          itself is an import you find out about later. */}
      {importing && (
        <div
          style={{
            background: T.panel,
            border: `1px solid ${T.accent}`,
            borderRadius: 10,
            padding: 14,
            marginBottom: 10,
          }}
        >
          <strong style={{ fontSize: 14 }}>
            {importing.matched.length} decision{importing.matched.length === 1 ? "" : "s"} ready to
            apply
          </strong>
          {!!importing.matched.length && (
            <div style={{ fontSize: 11, color: T.muted, marginTop: 6, maxHeight: 180, overflowY: "auto" }}>
              {importing.matched.slice(0, 40).map((m, n) => (
                <div key={n}>
                  {m.item.supplier} — <strong style={{ color: PRIORITY[m.priority]?.tone }}>{PRIORITY[m.priority]?.label}</strong>
                  {" · "}
                  {m.reason.slice(0, 90)}
                  {m.reason.length > 90 ? "…" : ""}
                </div>
              ))}
              {importing.matched.length > 40 && <div>…and {importing.matched.length - 40} more</div>}
            </div>
          )}

          {!!importing.unmatched.length && (
            <div style={{ fontSize: 11, color: T.warn, marginTop: 8 }}>
              <strong>{importing.unmatched.length} could not be tied to a bill</strong>
              {importing.unmatched.slice(0, 8).map((u, n) => (
                <div key={n}>
                  {u.supplier} ({u.where}) — {u.why}
                </div>
              ))}
            </div>
          )}

          {!!importing.problems.length && (
            <div style={{ fontSize: 11, color: T.cost, marginTop: 8 }}>
              <strong>{importing.problems.length} row{importing.problems.length === 1 ? "" : "s"} could not be read</strong>
              {importing.problems.slice(0, 8).map((p, n) => (
                <div key={n}>
                  {p.supplier} ({p.where}) — {p.why}
                </div>
              ))}
            </div>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button
              disabled={busy || !importing.matched.length}
              onClick={async () => {
                setBusy(true);
                setError(null);
                const failed = [];
                for (const m of importing.matched) {
                  try {
                    await savePaymentDecision(
                      docSource(m.item),
                      m.item.id,
                      m.priority,
                      m.reason,
                      session?.user?.email
                    );
                  } catch (err) {
                    failed.push(`${m.supplier}: ${err.message}`);
                  }
                }
                setImporting(null);
                setBusy(false);
                if (failed.length) setError(`${failed.length} could not be saved — ${failed[0]}`);
                await onChanged();
              }}
              style={{
                padding: "6px 14px",
                borderRadius: 6,
                border: "none",
                background: importing.matched.length ? T.accent : T.border,
                color: importing.matched.length ? "#fff" : T.muted,
                fontSize: 12,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {busy ? "Applying…" : `Apply ${importing.matched.length}`}
            </button>
            <button onClick={() => setImporting(null)} style={btn}>
              cancel
            </button>
          </div>
        </div>
      )}

      {/* Payment plans get a shape of their own. A plan is several dates and
          several amounts against one bill, and a table with one row per bill
          can only ever show the first and last of them. */}
      {filter === "plans" && (
        <PaymentPlans
          items={shown}
          busy={busy}
          onChange={async (fn) => {
            setBusy(true);
            setError(null);
            try {
              await fn();
              await onChanged();
            } catch (e) {
              setError(e.message || String(e));
            } finally {
              setBusy(false);
            }
          }}
        />
      )}

      <div style={{ background: T.panel, borderRadius: 10, overflowX: "auto", display: filter === "plans" ? "none" : undefined }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 30 }}>
                <input
                  type="checkbox"
                  checked={chosen.length === shown.length && shown.length > 0}
                  onChange={(e) =>
                    setSel(e.target.checked ? Object.fromEntries(shown.map((i) => [i.key, true])) : {})
                  }
                />
              </th>
              <th style={th}>Supplier</th>
              <th style={th}>Due</th>
              <th style={{ ...th, textAlign: "right" }}>Amount</th>
              <th style={th}>Paying on</th>
              <th style={{ ...th, width: 230 }}>Why it is being paid, or not</th>
              <th style={{ ...th, width: 60 }}>Paid</th>
              <th style={{ ...th, width: 90, textAlign: "right" }}>Remove</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((i) => (
              <tr
                key={i.key}
                style={{
                  borderTop: `1px solid ${T.borderSoft}`,
                  background: i.paid ? T.settledSoft : sel[i.key] ? T.accentSoft : undefined,
                  opacity: i.paid ? 0.7 : 1,
                }}
              >
                <td style={{ padding: "6px 10px" }}>
                  <input
                    type="checkbox"
                    checked={!!sel[i.key]}
                    onChange={(e) => setSel({ ...sel, [i.key]: e.target.checked })}
                  />
                </td>
                <td style={{ padding: "6px 10px", fontSize: 12 }}>
                  {i.supplier}
                  {i.docNo && (
                    <span style={{ fontFamily: fontMono, fontSize: 10, color: T.muted, marginLeft: 6 }}>
                      {i.docNo}
                    </span>
                  )}
                </td>
                <td style={{ padding: "6px 10px", fontSize: 11 }}>
                  {i.dueDate ? fmtDay(i.dueDate) : "—"}
                  {i.daysLate > 0 && !i.paid && (
                    <span
                      style={{
                        background: T.overdue,
                        color: "#fff",
                        borderRadius: 3,
                        padding: "0 4px",
                        marginLeft: 5,
                        fontSize: 9,
                        fontWeight: 700,
                      }}
                    >
                      {i.daysLate}d late
                    </span>
                  )}
                </td>
                <td style={{ padding: "6px 10px", textAlign: "right", fontFamily: fontMono, fontSize: 12, color: i.paid ? T.settled : T.cost }}>
                  {fmtMoney(-i.amount)}
                </td>
                <td style={{ padding: "6px 10px" }}>
                  <input
                    type="date"
                    // Uncontrolled and saved on blur: a date input reports a
                    // complete date after every keystroke, so saving per change
                    // stores the half-typed one.
                    key={`${i.key}-${i.expected}-${i.planned}`}
                    // Empty unless somebody actually chose this date.
                    //
                    // The forecast has to put an overdue bill somewhere, and it
                    // assumes the next payment run. Printing that assumption in
                    // the same box a real decision goes in made every unplanned
                    // bill look scheduled — the exact thing this page exists to
                    // tell apart. The assumption is still shown, underneath, as
                    // words rather than as an answer.
                    defaultValue={i.planned ? i.expected || "" : ""}
                    min="2000-01-01"
                    max="2099-12-31"
                    disabled={i.paid}
                    onBlur={async (e) => {
                      const v = e.target.value;
                      if (!v || v === i.expected) return;
                      if (!/^(20|21)\d{2}-\d{2}-\d{2}$/.test(v)) return;
                      setBusy(true);
                      await setDate(i, v);
                      await onChanged();
                      setBusy(false);
                    }}
                    style={{
                      fontFamily: fontMono,
                      fontSize: 11,
                      padding: "3px 5px",
                      border: `1px solid ${i.overridden ? T.accent : T.border}`,
                      borderRadius: 4,
                      background: i.overridden ? T.accentSoft : "transparent",
                      opacity: i.planned || i.paid ? 1 : 0.55,
                    }}
                  />
                  {!i.planned && !i.paid && !i.instalments?.length && (
                    <div style={{ fontSize: 10, color: T.muted, marginTop: 3 }}>
                      nothing agreed · assumed {fmtDay(i.expected)}
                    </div>
                  )}
                  {/* A bill on a plan does not have one date, so the date box
                      would be misleading on its own. The schedule says what is
                      actually going to happen. */}
                  {!!i.instalments?.length && (
                    <div style={{ fontSize: 10, color: T.accent, marginTop: 3 }}>
                      {i.instalments.length} payments ·{" "}
                      {fmtDay(i.instalments[0].due_date)} to{" "}
                      {fmtDay(i.instalments[i.instalments.length - 1].due_date)}
                    </div>
                  )}
                </td>
                <td style={{ padding: "6px 10px", verticalAlign: "top" }}>
                  <Decision item={i} busy={busy} onSave={saveDecision} onClear={dropDecision} />
                </td>
                <td style={{ padding: "6px 10px", textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={i.paid}
                    disabled={busy}
                    title="Mark as paid"
                    onChange={(e) => togglePaid(i, e.target.checked)}
                  />
                </td>
                <td style={{ padding: "6px 10px", textAlign: "right", whiteSpace: "nowrap" }}>
                  {i.dismissed ? (
                    <button
                      onClick={async () => {
                        setBusy(true);
                        await undismissDoc(docSource(i), i.id);
                        await onChanged();
                        setBusy(false);
                      }}
                      style={{ border: "none", background: "none", color: T.muted, fontSize: 10, cursor: "pointer", textDecoration: "underline" }}
                    >
                      dismissed · restore
                    </button>
                  ) : confirming === i.key ? (
                    <>
                      <button
                        onClick={() => removeOne(i)}
                        disabled={busy}
                        style={{ border: "none", background: T.cost, color: "#fff", borderRadius: 4, padding: "2px 7px", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}
                      >
                        {i.kind === "manual" ? "Delete" : "Dismiss"}
                      </button>
                      <button
                        onClick={() => setConfirming(null)}
                        style={{ border: "none", background: "none", color: T.muted, fontSize: 10, cursor: "pointer", marginLeft: 4 }}
                      >
                        cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setConfirming(i.key)}
                      title={i.kind === "manual" ? "Delete this imported bill" : "Keep it out of the forecast (it stays in Scoro)"}
                      style={{ border: "none", background: "none", color: T.muted, fontSize: 14, cursor: "pointer" }}
                    >
                      ×
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!shown.length && (
        <div style={{ fontSize: 12, color: T.muted, padding: 12 }}>Nothing to show.</div>
      )}
    </div>
  );
}
